import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bot, Send, ArrowLeft, Mic, MicOff, User, Zap } from 'lucide-react';
import { findBestMatch } from '../utils/nlp';
import faqsData from '../assets/faqs.json';
import { AI_API_BASE_URL } from '../utils/api';

// ── Web Speech API type declarations ─────────────────────────────
interface SpeechRecognitionEvent extends Event {
    readonly resultIndex: number;
    readonly results: SpeechRecognitionResultList;
}
interface SpeechRecognitionResultList {
    readonly length: number;
    item(index: number): SpeechRecognitionResult;
    [index: number]: SpeechRecognitionResult;
}
interface SpeechRecognitionResult {
    readonly isFinal: boolean;
    readonly length: number;
    item(index: number): SpeechRecognitionAlternative;
    [index: number]: SpeechRecognitionAlternative;
}
interface SpeechRecognitionAlternative {
    readonly transcript: string;
    readonly confidence: number;
}
interface SpeechRecognitionInstance extends EventTarget {
    continuous: boolean;
    interimResults: boolean;
    lang: string;
    maxAlternatives: number;
    start(): void;
    stop(): void;
    abort(): void;
    onresult: ((event: SpeechRecognitionEvent) => void) | null;
    onerror: ((event: Event) => void) | null;
    onend: ((event: Event) => void) | null;
    onstart: ((event: Event) => void) | null;
}
declare global {
    interface Window {
        SpeechRecognition: new () => SpeechRecognitionInstance;
        webkitSpeechRecognition: new () => SpeechRecognitionInstance;
    }
}

// ── Message types ─────────────────────────────────────────────────
interface Message {
    id: string;
    text: string;
    sender: 'user' | 'bot';
    timestamp: Date;
}

// ─────────────────────────────────────────────────────────────────
// Quick chip suggestions shown at the top / in empty state
// ─────────────────────────────────────────────────────────────────
const QUICK_CHIPS_EN = [
    { label: '🛠️ Services', query: 'What services do you provide?' },
    { label: '📞 Contact', query: 'How do I start a project with devNectar?' },
    { label: '👥 Team', query: 'How many employees are in the devNectar team?' },
    { label: '💻 Tech Stack', query: 'What technologies do you use for web development?' },
    { label: '🚀 Startups', query: 'Do you work with startups?' },
    { label: '💰 Pricing', query: 'What is your pricing model?' },
];

const QUICK_CHIPS_HI = [
    { label: '🛠️ सेवाएं', query: 'आप क्या सेवाएं प्रदान करते हैं?' },
    { label: '📞 संपर्क करें', query: 'devNectar के साथ प्रोजेक्ट कैसे शुरू करें?' },
    { label: '👥 टीम सदस्य', query: 'आपकी टीम में कितने सदस्य हैं?' },
    { label: '💻 तकनीक', query: 'आप वेब डेवलपमेंट के लिए क्या तकनीक इस्तेमाल करते हैं?' },
    { label: '📍 ऑफिस स्थान', query: 'devNectar कहां स्थित है?' },
    { label: '💰 शुल्क/प्राइस', query: 'आपकी फीस या कीमत क्या है?' },
];

// ─────────────────────────────────────────────────────────────────
// ChatbotPage — full-screen mobile experience
// ─────────────────────────────────────────────────────────────────
const ChatbotPage: React.FC = () => {
    const navigate = useNavigate();

    const [language, setLanguage] = useState<'en' | 'hi'>('en');
    const [messages, setMessages] = useState<Message[]>([
        {
            id: 'welcome',
            text: "Hi! 👋 I'm the devNectar assistant. Ask me anything about our team, services, or how to get started!",
            sender: 'bot',
            timestamp: new Date()
        }
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const [isLearning, setIsLearning] = useState(false);
    const [learningQuestion, setLearningQuestion] = useState('');
    const [isListening, setIsListening] = useState(false);
    const [voiceSupported, setVoiceSupported] = useState(false);
    const [interimTranscript, setInterimTranscript] = useState('');

    const handleLanguageToggle = (newLang: 'en' | 'hi') => {
        setLanguage(newLang);
        const welcomeText = newLang === 'hi'
            ? "नमस्ते! 👋 मैं devNectar सहायक हूँ। मुझसे हमारी टीम, सेवाओं या किसी भी सवाल के बारे में पूछें!"
            : "Hi! 👋 I'm the devNectar assistant. Ask me anything about our team, services, or how to get started!";

        setMessages(prev => {
            if (prev.length <= 1) {
                return [{ id: 'welcome', text: welcomeText, sender: 'bot', timestamp: new Date() }];
            }
            return prev;
        });
    };

    const messagesEndRef = useRef<HTMLDivElement>(null);
    const inputRef = useRef<HTMLInputElement>(null);
    const recognitionRef = useRef<SpeechRecognitionInstance | null>(null);
    const dispatchRef = useRef<(text: string) => void>(() => { });

    // ── Scroll to bottom ──────────────────────────────────────────
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages, isTyping]);

    // ── Check voice support ───────────────────────────────────────
    useEffect(() => {
        const API = window.SpeechRecognition || window.webkitSpeechRecognition;
        setVoiceSupported(!!API);
    }, []);

    // ── Core send / dispatch ──────────────────────────────────────
    const handleSend = useCallback(async (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;

        const userMsg: Message = {
            id: Date.now().toString(),
            text: trimmed,
            sender: 'user',
            timestamp: new Date()
        };

        setMessages(prev => [...prev, userMsg]);
        setInputValue('');
        setInterimTranscript('');
        setIsTyping(true);

        if (isLearning) {
            try {
                await fetch(`${AI_API_BASE_URL}/chatbot/learn`, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ question: learningQuestion, answer: trimmed })
                });

                const botMsg: Message = {
                    id: (Date.now() + 1).toString(),
                    text: "Thank you! I've learned the answer. Ask me another question.",
                    sender: 'bot',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, botMsg]);
            } catch (error) {
                const botMsg: Message = {
                    id: (Date.now() + 1).toString(),
                    text: "Failed to save the answer. Please try again.",
                    sender: 'bot',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, botMsg]);
            }
            setIsLearning(false);
            setLearningQuestion('');
            setIsTyping(false);
            return;
        }

        try {
            const response = await fetch(`${AI_API_BASE_URL}/chatbot/query`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ question: trimmed })
            });
            const data = await response.json();

            if (data.isUnknown) {
                setIsLearning(true);
                setLearningQuestion(trimmed);
            }

            if (data.redirect) {
                const botMsg: Message = {
                    id: (Date.now() + 1).toString(),
                    text: `${data.answer}. Taking you there now...`,
                    sender: 'bot',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, botMsg]);
                setIsTyping(false);
                
                setTimeout(() => {
                    if (data.redirect?.startsWith('http')) {
                        window.open(data.redirect, '_blank');
                    } else {
                        navigate(data.redirect!);
                    }
                }, 1500);
            } else {
                const botMsg: Message = {
                    id: (Date.now() + 1).toString(),
                    text: data.answer,
                    sender: 'bot',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, botMsg]);
                setIsTyping(false);
            }
        } catch (error) {
            console.warn("Backend offline, falling back to local.");
            const localMatch = findBestMatch(trimmed, faqsData as any[]);
            if (localMatch) {
                const botMsg: Message = {
                    id: (Date.now() + 1).toString(),
                    text: localMatch.answer,
                    sender: 'bot',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, botMsg]);
            } else {
                const fallbackMsg: Message = {
                    id: (Date.now() + 1).toString(),
                    text: "Sorry, I don't have this knowledge in my knowledge base. Please contact our team for further help.",
                    sender: 'bot',
                    timestamp: new Date()
                };
                setMessages(prev => [...prev, fallbackMsg]);
            }
            setIsTyping(false);
        }
    }, [navigate, isLearning, learningQuestion]);

    // Keep dispatch ref current to avoid stale closures in voice callbacks
    useEffect(() => { dispatchRef.current = (text: string) => handleSend(text); }, [handleSend]);

    // ── Voice: start / stop ───────────────────────────────────────
    const startListening = () => {
        const API = window.SpeechRecognition || window.webkitSpeechRecognition;
        if (!API) return;

        recognitionRef.current?.abort();
        const recognition = new API();
        recognition.lang = language === 'hi' ? 'hi-IN' : 'en-US';
        recognition.continuous = true;
        recognition.interimResults = true;
        recognition.maxAlternatives = 1;

        recognition.onstart = () => { setIsListening(true); setInterimTranscript(''); };

        recognition.onresult = (event: SpeechRecognitionEvent) => {
            let interim = '';
            let finalText = '';
            for (let i = event.resultIndex; i < event.results.length; i++) {
                const result = event.results[i];
                if (!result) continue;
                const alt = result[0];
                if (!alt) continue;
                if (result.isFinal) finalText += alt.transcript;
                else interim += alt.transcript;
            }

            if (interim) { setInterimTranscript(interim); setInputValue(interim); }

            let cleanFinalText = finalText.trim();
            if (cleanFinalText) {
                cleanFinalText = cleanFinalText.replace(/\b(dev\s*nectar|dev\s*nector|dave\s*nectar|death\s*nectar|def\s*nectar|deb\s*nectar)\b/gi, 'devNectar');
                setInterimTranscript('');
                setInputValue('');
                dispatchRef.current(cleanFinalText);
                recognition.stop();
            }
        };

        recognition.onerror = () => { setIsListening(false); setInterimTranscript(''); };
        recognition.onend = () => { setIsListening(false); setInterimTranscript(''); };

        recognitionRef.current = recognition;
        recognition.start();
    };

    const stopListening = () => {
        recognitionRef.current?.stop();
        setIsListening(false);
        setInterimTranscript('');
    };

    const toggleListening = () => isListening ? stopListening() : startListening();

    // ─────────────────────────────────────────────────────────────
    // RENDER
    // ─────────────────────────────────────────────────────────────
    return (
        <div className="chatbot-page">

            {/* ── Top App Bar ─────────────────────────────────── */}
            <header className="chatbot-header">
                <button
                    onClick={() => navigate(-1)}
                    className="chatbot-back-btn"
                    aria-label="Go back"
                >
                    <ArrowLeft size={22} />
                </button>

                <div className="chatbot-header-info">
                    <div className="chatbot-avatar">
                        <Bot size={20} className="text-white" />
                        <span className="chatbot-avatar-dot" />
                    </div>
                    <div>
                        <p className="chatbot-name">devNectar Assistant</p>
                        <p className="chatbot-status">
                            {isListening ? (language === 'hi' ? '🎤 सुन रहा हूँ…' : '🎤 Listening…') : (language === 'hi' ? 'ऑनलाइन' : 'Online')}
                        </p>
                    </div>
                </div>

                <div className="flex items-center gap-2">
                    {/* Language Selector Toggle */}
                    <div className="flex items-center bg-blue-900/40 p-0.5 rounded-lg border border-white/20">
                        <button
                            type="button"
                            onClick={() => handleLanguageToggle('en')}
                            className={`px-2 py-1 text-xs font-bold rounded transition-all ${language === 'en' ? 'bg-white text-blue-800 shadow-sm' : 'text-white/80 hover:text-white'}`}
                        >
                            EN
                        </button>
                        <button
                            type="button"
                            onClick={() => handleLanguageToggle('hi')}
                            className={`px-2 py-1 text-xs font-bold rounded transition-all ${language === 'hi' ? 'bg-white text-blue-800 shadow-sm' : 'text-white/80 hover:text-white'}`}
                        >
                            हिंदी
                        </button>
                    </div>

                    <div className="chatbot-header-icon">
                        <Zap size={18} />
                    </div>
                </div>
            </header>

            {/* ── Messages ────────────────────────────────────── */}
            <main className="chatbot-messages">

                {/* Date label */}
                <div className="chatbot-date-label">
                    {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </div>

                {messages.map((msg) => (
                    <div
                        key={msg.id}
                        className={`chatbot-bubble-row ${msg.sender === 'user' ? 'user-row' : 'bot-row'}`}
                    >
                        {msg.sender === 'bot' && (
                            <div className="bot-icon-wrap">
                                <Bot size={15} className="text-blue-600" />
                            </div>
                        )}
                        <div className={`chatbot-bubble ${msg.sender === 'user' ? 'user-bubble' : 'bot-bubble'}`}>
                            <p>{msg.text}</p>
                            <span className="chatbot-time">
                                {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                            </span>
                        </div>
                        {msg.sender === 'user' && (
                            <div className="user-icon-wrap">
                                <User size={15} className="text-gray-500" />
                            </div>
                        )}
                    </div>
                ))}

                {/* Typing indicator */}
                {isTyping && (
                    <div className="chatbot-bubble-row bot-row">
                        <div className="bot-icon-wrap">
                            <Bot size={15} className="text-blue-600" />
                        </div>
                        <div className="bot-bubble chatbot-bubble typing-bubble">
                            <span /><span /><span />
                        </div>
                    </div>
                )}

                {/* Live voice transcript */}
                {isListening && interimTranscript && (
                    <div className="chatbot-bubble-row user-row">
                        <div className="user-bubble chatbot-bubble interim-bubble">
                            <Mic size={12} className="inline mr-1 text-blue-400" />
                            <em>{interimTranscript}…</em>
                        </div>
                        <div className="user-icon-wrap">
                            <Mic size={15} className="text-blue-500" />
                        </div>
                    </div>
                )}

                <div ref={messagesEndRef} />
            </main>

            {/* ── Quick Chips ──────────────────────────────────── */}
            <div className="chatbot-chips">
                {(language === 'hi' ? QUICK_CHIPS_HI : QUICK_CHIPS_EN).map(chip => (
                    <button
                        key={chip.label}
                        className="chatbot-chip"
                        onClick={() => handleSend(chip.query)}
                    >
                        {chip.label}
                    </button>
                ))}
            </div>

            {/* ── Input Bar ───────────────────────────────────── */}
            <footer className="chatbot-input-bar">
                {voiceSupported && (
                    <button
                        type="button"
                        onClick={toggleListening}
                        className={`chatbot-mic-btn ${isListening ? 'listening' : ''}`}
                        aria-label={isListening ? 'Stop listening' : 'Speak'}
                    >
                        {isListening ? <MicOff size={20} /> : <Mic size={20} />}
                    </button>
                )}

                <input
                    ref={inputRef}
                    type="text"
                    value={inputValue}
                    onChange={e => setInputValue(e.target.value)}
                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleSend(inputValue); } }}
                    placeholder={isListening ? (language === 'hi' ? 'सुन रहा हूँ… बोलिए 🎤' : 'Listening… speak now 🎤') : (language === 'hi' ? 'मुझसे कुछ भी पूछें…' : 'Ask me anything…')}
                    className={`chatbot-input ${isListening ? 'input-listening' : ''}`}
                />

                <button
                    type="button"
                    onClick={() => handleSend(inputValue)}
                    className="chatbot-send-btn"
                    aria-label="Send message"
                >
                    <Send size={20} />
                </button>
            </footer>

            {isListening && (
                <p className="chatbot-voice-hint">
                    {language === 'hi' ? '🎤 साफ़ बोलें और रुकें — उत्तर अपने आप भेजा जाएगा' : '🎤 Speak clearly then pause — I\'ll send it automatically'}
                </p>
            )}

            <p className="text-[11px] text-gray-400 font-medium text-center my-2 tracking-wide select-none">
                Powered by{' '}
                <a
                    href="https://devnectar.in"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="font-semibold text-blue-600 hover:text-blue-700 hover:underline cursor-pointer transition-colors"
                >
                    devNectar
                </a>
            </p>

            {/* ── Scoped Styles ───────────────────────────────── */}
            <style>{`
                /* ── Layout ── */
                .chatbot-page {
                    display: flex;
                    flex-direction: column;
                    height: 100dvh;         /* dynamic viewport height (handles mobile toolbars) */
                    background: #f0f4ff;
                    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
                    overflow: hidden;
                }

                /* ── Header ── */
                .chatbot-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    padding: 14px 16px;
                    background: linear-gradient(135deg, #1d4ed8 0%, #1e3a8a 100%);
                    color: white;
                    flex-shrink: 0;
                    box-shadow: 0 2px 12px rgba(29,78,216,0.3);
                    /* iOS safe area */
                    padding-top: max(14px, env(safe-area-inset-top));
                }
                .chatbot-back-btn {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 38px;
                    height: 38px;
                    background: rgba(255,255,255,0.15);
                    border-radius: 50%;
                    border: none;
                    color: white;
                    cursor: pointer;
                    flex-shrink: 0;
                    transition: background 0.2s;
                }
                .chatbot-back-btn:active { background: rgba(255,255,255,0.3); }
                .chatbot-header-info {
                    display: flex;
                    align-items: center;
                    gap: 10px;
                    flex: 1;
                }
                .chatbot-avatar {
                    position: relative;
                    width: 40px;
                    height: 40px;
                    border-radius: 50%;
                    background: rgba(255,255,255,0.2);
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }
                .chatbot-avatar-dot {
                    position: absolute;
                    bottom: 2px;
                    right: 2px;
                    width: 10px;
                    height: 10px;
                    background: #22c55e;
                    border-radius: 50%;
                    border: 2px solid #1d4ed8;
                }
                .chatbot-name   { font-weight: 700; font-size: 15px; line-height: 1.2; }
                .chatbot-status { font-size: 11px; color: rgba(255,255,255,0.75); }
                .chatbot-header-icon {
                    width: 36px;
                    height: 36px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    background: rgba(255,255,255,0.1);
                    border-radius: 50%;
                    color: rgba(255,255,255,0.8);
                }

                /* ── Messages area ── */
                .chatbot-messages {
                    flex: 1;
                    overflow-y: auto;
                    padding: 16px 14px 8px;
                    display: flex;
                    flex-direction: column;
                    gap: 10px;
                    -webkit-overflow-scrolling: touch;
                    overscroll-behavior: contain;
                }
                .chatbot-date-label {
                    text-align: center;
                    font-size: 11px;
                    color: #9ca3af;
                    background: rgba(156,163,175,0.15);
                    padding: 4px 12px;
                    border-radius: 20px;
                    margin-bottom: 4px;
                    align-self: center;
                }

                /* ── Bubble rows ── */
                .chatbot-bubble-row {
                    display: flex;
                    align-items: flex-end;
                    gap: 6px;
                    max-width: 88%;
                    animation: slideIn 0.25s ease;
                }
                .bot-row  { align-self: flex-start; }
                .user-row { align-self: flex-end; flex-direction: row-reverse; }

                @keyframes slideIn {
                    from { opacity: 0; transform: translateY(8px); }
                    to   { opacity: 1; transform: translateY(0); }
                }

                .bot-icon-wrap, .user-icon-wrap {
                    width: 28px;
                    height: 28px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                    margin-bottom: 2px;
                }
                .bot-icon-wrap  { background: #dbeafe; }
                .user-icon-wrap { background: #e5e7eb; }

                /* ── Bubbles ── */
                .chatbot-bubble {
                    padding: 10px 14px;
                    border-radius: 18px;
                    font-size: 14.5px;
                    line-height: 1.45;
                    word-break: break-word;
                    max-width: 100%;
                }
                .bot-bubble {
                    background: white;
                    color: #1f2937;
                    border-radius: 4px 18px 18px 18px;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.08);
                }
                .user-bubble {
                    background: linear-gradient(135deg, #2563eb, #1d4ed8);
                    color: white;
                    border-radius: 18px 4px 18px 18px;
                    box-shadow: 0 2px 8px rgba(37,99,235,0.3);
                }
                .chatbot-time {
                    display: block;
                    font-size: 10px;
                    margin-top: 4px;
                    opacity: 0.55;
                    text-align: right;
                }

                /* ── Typing animation ── */
                .typing-bubble {
                    display: flex;
                    gap: 5px;
                    padding: 12px 16px;
                }
                .typing-bubble span {
                    width: 7px;
                    height: 7px;
                    background: #93c5fd;
                    border-radius: 50%;
                    animation: typingDot 1.2s infinite ease-in-out;
                }
                .typing-bubble span:nth-child(2) { animation-delay: 0.2s; }
                .typing-bubble span:nth-child(3) { animation-delay: 0.4s; }
                @keyframes typingDot {
                    0%, 60%, 100% { transform: translateY(0);    opacity: 0.5; }
                    30%           { transform: translateY(-5px);  opacity: 1;   }
                }

                /* ── Interim voice bubble ── */
                .interim-bubble {
                    background: linear-gradient(135deg, #dbeafe, #bfdbfe);
                    color: #1d4ed8;
                    font-size: 13px;
                    border-radius: 18px 4px 18px 18px;
                    opacity: 0.85;
                    border: 1.5px dashed #93c5fd;
                }

                /* ── Quick Chips ── */
                .chatbot-chips {
                    display: flex;
                    gap: 8px;
                    padding: 8px 14px;
                    overflow-x: auto;
                    flex-shrink: 0;
                    background: #f0f4ff;
                    -webkit-overflow-scrolling: touch;
                    scrollbar-width: none;
                }
                .chatbot-chips::-webkit-scrollbar { display: none; }
                .chatbot-chip {
                    white-space: nowrap;
                    padding: 6px 14px;
                    background: white;
                    border: 1.5px solid #bfdbfe;
                    border-radius: 20px;
                    font-size: 12.5px;
                    color: #1d4ed8;
                    cursor: pointer;
                    transition: all 0.18s;
                    flex-shrink: 0;
                    font-weight: 500;
                }
                .chatbot-chip:active {
                    background: #dbeafe;
                    border-color: #1d4ed8;
                    transform: scale(0.96);
                }

                /* ── Input bar ── */
                .chatbot-input-bar {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    padding: 10px 14px;
                    background: white;
                    border-top: 1px solid #e5e7eb;
                    flex-shrink: 0;
                    /* iOS safe area for bottom home bar */
                    padding-bottom: max(10px, env(safe-area-inset-bottom));
                }
                .chatbot-input {
                    flex: 1;
                    padding: 11px 16px;
                    background: #f3f4f6;
                    border: 1.5px solid #e5e7eb;
                    border-radius: 24px;
                    font-size: 14.5px;
                    outline: none;
                    transition: border-color 0.2s, background 0.2s;
                    color: #111827;
                }
                .chatbot-input:focus {
                    border-color: #3b82f6;
                    background: #fff;
                }
                .input-listening {
                    border-color: #3b82f6 !important;
                    background: #eff6ff !important;
                    color: #1d4ed8;
                }

                /* ── Mic button ── */
                .chatbot-mic-btn {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    cursor: pointer;
                    transition: all 0.2s;
                    flex-shrink: 0;
                    background: #f3f4f6;
                    color: #6b7280;
                }
                .chatbot-mic-btn:active { transform: scale(0.92); }
                .chatbot-mic-btn.listening {
                    background: #3b82f6;
                    color: white;
                    animation: micPulse 1s infinite;
                }
                @keyframes micPulse {
                    0%,100% { box-shadow: 0 0 0 0 rgba(59,130,246,0.45); }
                    50%     { box-shadow: 0 0 0 10px rgba(59,130,246,0); }
                }

                /* ── Send button ── */
                .chatbot-send-btn {
                    width: 44px;
                    height: 44px;
                    border-radius: 50%;
                    background: linear-gradient(135deg, #2563eb, #1d4ed8);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    border: none;
                    cursor: pointer;
                    flex-shrink: 0;
                    transition: transform 0.15s;
                    box-shadow: 0 2px 8px rgba(37,99,235,0.35);
                }
                .chatbot-send-btn:active { transform: scale(0.9); }

                /* ── Voice hint ── */
                .chatbot-voice-hint {
                    text-align: center;
                    font-size: 11px;
                    color: #3b82f6;
                    padding: 0 16px 6px;
                    animation: pulse 2s infinite;
                    flex-shrink: 0;
                }
                @keyframes pulse {
                    0%,100% { opacity: 1; }
                    50%     { opacity: 0.6; }
                }

                /* ── Scrollbar ── */
                .chatbot-messages::-webkit-scrollbar { width: 4px; }
                .chatbot-messages::-webkit-scrollbar-thumb {
                    background: #d1d5db;
                    border-radius: 4px;
                }
            `}</style>
        </div>
    );
};

export default ChatbotPage;
